import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { coreConfig } from '../config/core';

export const ONBOARDING_STATE_KEY = 'passport_onboarding_state';
export const ONBOARDING_CALLBACK_PATH = '/auth/callback';

let onboardingCallbackHandled = false;

export function hasHandledOnboardingCallback(): boolean {
  return onboardingCallbackHandled;
}

export function markOnboardingCallbackHandled(): void {
  onboardingCallbackHandled = true;
}

export function resetOnboardingCallbackGuard(): void {
  onboardingCallbackHandled = false;
}

export type OnboardingCallbackStatus =
  | 'code'
  | 'cancelled'
  | 'error'
  | 'state_mismatch'
  | 'ignored';

export interface OnboardingCallback {
  url: string;
  status: OnboardingCallbackStatus;
  code: string | null;
  state: string | null;
  error: string | null;
}

export function createHostedOnboardingState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function storeOnboardingState(state: string): void {
  sessionStorage.setItem(ONBOARDING_STATE_KEY, state);
}

export function getStoredOnboardingState(): string | null {
  return sessionStorage.getItem(ONBOARDING_STATE_KEY);
}

export function clearOnboardingState(): void {
  sessionStorage.removeItem(ONBOARDING_STATE_KEY);
}

export function getOnboardingReturnUrl(): string {
  if (Capacitor.isNativePlatform()) {
    return coreConfig.onboardingNativeReturnUrl;
  }
  if (coreConfig.onboardingReturnUrl) {
    return coreConfig.onboardingReturnUrl;
  }
  return `${window.location.origin}${ONBOARDING_CALLBACK_PATH}`;
}

export function buildHostedOnboardingUrl(state: string): string {
  const origin = coreConfig.coreFrontendOrigin;
  if (!origin) {
    throw new Error('VITE_CORE_FRONTEND_ORIGIN is not configured.');
  }
  if (!coreConfig.applicationId) {
    throw new Error('VITE_APPLICATION_ID is not configured.');
  }

  const params = new URLSearchParams({
    returnUrl: getOnboardingReturnUrl(),
    state,
  });

  return `${origin}/#/onboarding/${coreConfig.applicationId}?${params.toString()}`;
}

export async function startHostedOnboarding(): Promise<void> {
  resetOnboardingCallbackGuard();
  const state = createHostedOnboardingState();
  storeOnboardingState(state);
  const url = buildHostedOnboardingUrl(state);

  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, presentationStyle: 'fullscreen' });
    return;
  }

  window.location.assign(url);
}

export async function closeHostedOnboardingBrowser(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Browser.close();
  } catch {
    // Browser may already be dismissed
  }
}

export function isOnboardingCallbackUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;

  if (/#\/onboarding\//i.test(trimmed)) {
    return false;
  }

  if (/^passport(?:-dev|-stage)?:/i.test(trimmed)) {
    return /auth\/callback/i.test(trimmed);
  }

  if (!/^https?:/i.test(trimmed)) {
    return /(?:^|\/\/)auth\/callback(?:[/?#]|$)/i.test(trimmed);
  }

  try {
    const parsed = new URL(trimmed);
    const path = normalizePath(parsed.pathname);
    return path === ONBOARDING_CALLBACK_PATH || path.endsWith(ONBOARDING_CALLBACK_PATH);
  } catch {
    return /\/auth\/callback(?:[/?#]|$)/i.test(trimmed);
  }
}

export function queryParamsFromUrl(url: string): URLSearchParams {
  const value = decodeOnce(url.trim());
  const hashIndex = value.indexOf('#');
  const beforeHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const hash = hashIndex >= 0 ? value.slice(hashIndex + 1) : '';

  const hashQueryIndex = hash.indexOf('?');
  if (hashQueryIndex >= 0) {
    const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
    if (hasHandoffParam(hashParams)) {
      return hashParams;
    }
  }

  const queryIndex = beforeHash.indexOf('?');
  if (queryIndex >= 0) {
    return new URLSearchParams(beforeHash.slice(queryIndex + 1));
  }

  return new URLSearchParams();
}

export function parseOnboardingCallback(
  url: string,
  expectedState: string | null = getStoredOnboardingState(),
): OnboardingCallback {
  const params = queryParamsFromUrl(url);
  const code = emptyToNull(params.get('code'));
  const state = emptyToNull(params.get('state'));
  const error = emptyToNull(params.get('error') ?? params.get('status'));

  let status: OnboardingCallbackStatus = 'ignored';
  if (error && isCancelError(error)) {
    status = 'cancelled';
  } else if (expectedState && state && state !== expectedState) {
    status = 'state_mismatch';
  } else if (code) {
    status = 'code';
  } else if (error) {
    status = 'error';
  }

  return { url, status, code, state, error };
}

export function toInternalCallbackPath(url: string): string | null {
  if (!isOnboardingCallbackUrl(url)) return null;
  const query = queryParamsFromUrl(url).toString();
  return query ? `${ONBOARDING_CALLBACK_PATH}?${query}` : ONBOARDING_CALLBACK_PATH;
}

function hasHandoffParam(params: URLSearchParams): boolean {
  return Boolean(params.get('code') || params.get('error') || params.get('status'));
}

function isCancelError(error: string): boolean {
  const normalized = error.trim().toLowerCase();
  return (
    normalized === 'cancel' ||
    normalized === 'cancelled' ||
    normalized === 'canceled' ||
    normalized === 'access_denied'
  );
}

function emptyToNull(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function decodeOnce(url: string): string {
  try {
    return decodeURIComponent(url);
  } catch {
    return url;
  }
}
